// ─── MotionPicker ──────────────────────────────────────────────────────────
// Dropdown custom para elegir un preset de animación (entrada / salida) de
// un clip. Reemplaza al `<select>` nativo porque necesitamos mostrar una
// mini preview animada de cada preset al hacer hover.
//
// Cada opción es una tile compacta con:
//   • un box de preview que reproduce el preset en loop mientras el mouse
//     está encima (usa `computeClipMotion` con un `localTime` animado por
//     requestAnimationFrame),
//   • el nombre del preset abajo.
//
// Para los presets per-item (typewriter / cascade) la preview splitea el
// texto "Aa" / "Hola" en spans y aplica `itemPreset.styleAt` — así el
// usuario ve exactamente el efecto, no una versión simplificada.
//
// Cierre del popover: click afuera o Escape. El trigger es un botón con
// el label actual y un chevron. Posicionamiento fijo debajo del trigger
// (abajo si hay espacio, sino arriba).

import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  CLIP_MOTION_PRESETS,
  computeClipMotion,
  splitTextForMotion,
  motionOptionsFor,
} from '../animations/clipMotion';

// Duración de la ventana de entrada/salida usada en el preview. 0.7s da
// un loop fluido — no tan rápido que parezca un flicker, ni tan lento
// que el usuario se aburra antes de ver el final.
const PREVIEW_DUR = 0.7;
// Pausa entre loops para que se perciba el estado "steady" del clip.
const PREVIEW_HOLD = 0.45;

type Kind = 'enter' | 'exit';

export type MotionPickerProps = {
  value: string;
  onChange: (id: string) => void;
  kind: Kind;
  /** 'text' incluye presets text-only (typewriter, cascade); 'common' los omite. */
  scope: 'text' | 'common';
  /** Texto mostrado encima del botón (ej. "Entrada"). Opcional: si se
   *  omite, no se renderiza (útil en toolbars compactos donde el
   *  contexto ya comunica qué controla el dropdown). */
  label?: string;
  /** Prefijo breve dentro del trigger (p. ej. "In" / "Out") para que en
   *  toolbars sin label el usuario aún sepa cuál es cuál. */
  shortLabel?: string;
  /** Clase extra aplicada al contenedor raíz — sirve para fijar ancho
   *  desde el call site (p. ej. `w-[120px]` en la toolbar). */
  className?: string;
  /** Alineación del popover respecto al trigger. Default 'stretch'
   *  (mismo ancho que el trigger); en toolbars compactos conviene
   *  'left' o 'right' para que el grid de opciones no se deforme. */
  popoverAlign?: 'stretch' | 'left' | 'right';
};

// Hook: ticker de tiempo que arranca cuando `active` es true y para al
// soltarlo. Devuelve `localTime` en segundos que va creciendo y se
// resetea cuando pasa `PREVIEW_DUR + PREVIEW_HOLD`. Usado por cada tile
// hovered y por el trigger (que previsualiza el preset seleccionado).
function usePreviewTicker(active: boolean): number {
  const [t, setT] = useState(0);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    if (!active) {
      setT(0);
      return;
    }
    startRef.current = performance.now();
    const total = PREVIEW_DUR + PREVIEW_HOLD;
    const loop = () => {
      const elapsed = (performance.now() - startRef.current) / 1000;
      setT(elapsed % total);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active]);

  return t;
}

// Renderiza el texto de muestra con el motion aplicado para un preset y
// un `localTime` dado. Si el preset es per-item partimos el texto en
// spans; si no, aplicamos el estilo al wrapper directamente.
const PreviewText: React.FC<{
  presetId: string;
  kind: Kind;
  localTime: number;
  sample: string;
}> = ({ presetId, kind, localTime, sample }) => {
  // Simulamos un clip de `2s`. Para enter mostramos `[0, PREVIEW_DUR]`;
  // para exit reposicionamos el tiempo al tramo final del clip.
  const clipDur = 2;
  const effectiveLocal =
    kind === 'enter'
      ? Math.min(localTime, PREVIEW_DUR)
      : clipDur - PREVIEW_DUR + Math.min(localTime, PREVIEW_DUR);

  const motion = computeClipMotion({
    localTime: effectiveLocal,
    duration: clipDur,
    enterId: kind === 'enter' ? presetId : 'none',
    exitId: kind === 'exit' ? presetId : 'none',
    enterDur: PREVIEW_DUR,
    exitDur: PREVIEW_DUR,
  });

  if (motion.itemPreset) {
    const items = splitTextForMotion(sample, motion.itemPreset.splitMode);
    return (
      <span className="whitespace-pre">
        {items.map((item, i) => (
          <span key={i} style={motion.itemPreset!.styleAt(i, items.length)}>
            {item === ' ' ? '\u00A0' : item}
          </span>
        ))}
      </span>
    );
  }
  return <span style={motion.wrapperStyle}>{sample}</span>;
};

// Una tile del grid: preview box + label. Se encarga de su propio hover
// y su propio ticker.
const PresetTile: React.FC<{
  presetId: string;
  label: string;
  kind: Kind;
  selected: boolean;
  onSelect: () => void;
}> = ({ presetId, label, kind, selected, onSelect }) => {
  const [hovering, setHovering] = useState(false);
  // Si el preset es text-only usamos "Hola" para que el split por
  // palabras tenga algo que splitear; para el resto, "Aa" alcanza y
  // queda más compacto.
  const preset = CLIP_MOTION_PRESETS.find(p => p.id === presetId);
  const sample = preset?.scope === 'text' ? 'Hola' : 'Aa';
  const localTime = usePreviewTicker(hovering && presetId !== 'none');

  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={`group relative flex flex-col items-stretch gap-1 rounded-md border p-2 text-left transition-colors ${
        selected
          ? 'border-brand-primary bg-brand-primary/5'
          : 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50'
      }`}
    >
      <div className="flex h-10 items-center justify-center overflow-hidden rounded bg-neutral-50 text-[14px] font-medium text-neutral-800">
        {presetId === 'none' ? (
          <span className="text-[11px] text-neutral-400">—</span>
        ) : (
          <PreviewText
            presetId={presetId}
            kind={kind}
            localTime={localTime}
            sample={sample}
          />
        )}
      </div>
      <span
        className={`truncate text-[11.5px] ${
          selected ? 'font-medium text-brand-primary' : 'text-neutral-700'
        }`}
      >
        {label}
      </span>
    </button>
  );
};

export const MotionPicker: React.FC<MotionPickerProps> = ({
  value,
  onChange,
  kind,
  scope,
  label,
  shortLabel,
  className,
  popoverAlign = 'stretch',
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const options = useMemo(() => motionOptionsFor(scope), [scope]);
  const current = options.find(o => o.id === value) ?? options[0];

  // Click afuera para cerrar. Usamos `mousedown` y no `click` para que
  // el popover no re-aparezca si el usuario clickea afuera muy rápido.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Loop sutil en el trigger cuando está cerrado y tiene un preset ≠ none,
  // así el label se refuerza con una preview en miniatura al costado.
  const [triggerHover, setTriggerHover] = useState(false);
  const triggerT = usePreviewTicker(triggerHover && value !== 'none' && !open);

  const handleSelect = useCallback(
    (id: string) => {
      onChange(id);
      setOpen(false);
    },
    [onChange],
  );

  // Posicionamiento del popover. 'stretch' = mismo ancho que trigger
  // (comportamiento del panel lateral). 'left' y 'right' lo anclan a un
  // borde y lo dejan crecer hasta su `minWidth` — es lo que queremos en
  // la toolbar superior, donde el trigger es angosto.
  const popoverPos =
    popoverAlign === 'stretch'
      ? 'left-0 right-0'
      : popoverAlign === 'right'
      ? 'right-0'
      : 'left-0';

  return (
    <div ref={rootRef} data-clip-ui="true" className={`relative ${className ?? ''}`}>
      {label && (
        <label className="mb-1.5 block text-[12px] font-medium text-brand-black/70">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setTriggerHover(true)}
        onMouseLeave={() => setTriggerHover(false)}
        className={`flex h-8 w-full items-center gap-1.5 rounded-md border bg-white px-1.5 text-[13px] transition-colors ${
          open
            ? 'border-brand-primary ring-1 ring-brand-primary/20'
            : 'border-neutral-200 hover:border-neutral-300'
        }`}
      >
        {/* Mini-preview del preset seleccionado (solo si no es "none"). */}
        <div className="flex h-5 w-6 flex-none items-center justify-center overflow-hidden rounded bg-neutral-50 text-[11px] font-medium text-neutral-700">
          {value === 'none' ? (
            <span className="text-neutral-300">—</span>
          ) : (
            <PreviewText
              presetId={value}
              kind={kind}
              localTime={triggerT}
              sample="Aa"
            />
          )}
        </div>
        {shortLabel && (
          <span className="flex-none text-[11px] font-medium uppercase tracking-wider text-neutral-400">
            {shortLabel}
          </span>
        )}
        <span className="flex-1 truncate text-left text-neutral-900">
          {current.label}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 flex-none text-neutral-400 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div
          className={`absolute ${popoverPos} top-full z-30 mt-1 rounded-lg border border-neutral-200 bg-white p-2 shadow-lg`}
          // El ancho mínimo evita que el popover se vea apretado cuando
          // el trigger es angosto (caso side-by-side enter/exit o la
          // toolbar superior, donde el trigger es un pill compacto).
          style={{ minWidth: 240 }}
        >
          <div className="grid grid-cols-2 gap-1.5">
            {options.map(o => (
              <PresetTile
                key={o.id}
                presetId={o.id}
                label={o.label}
                kind={kind}
                selected={o.id === value}
                onSelect={() => handleSelect(o.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
